<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { randomId } from '@/functions'

  const props = withDefaults(
    defineProps<{
      modelValue: number | string | null,
      id?: string,
      label?: string,
      placeholder?: string,
      required?: boolean,
      disabled?: boolean,
      error?: string | string[],
      min?: number,
      max?: number,
      suffix?: string,
      note?: string,
    }>(),
    {
      modelValue: null,
      id: undefined,
      label: undefined,
      placeholder: 'cth: 1,5',
      required: false,
      disabled: false,
      error: undefined,
      min: 0,
      max: 100,
      suffix: '%',
      note: 'Gunakan tanda koma (,) untuk desimal, contoh: 1,5',
    },
  )

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number | null): void,
  }>()

  const defaultId = randomId()
  const inputId = computed(() => props.id ?? defaultId)

  const formatToDisplay = (val: number | string | null | undefined): string => {
    if (val === null || val === undefined || val === '') return ''
    const str = String(val).replace('.', ',')
    return str
  }

  const parseToNumber = (val: string): number | null => {
    if (!val || val.trim() === '') return null
    // Replace comma with dot
    const clean = val.trim().replace(',', '.')
    const parsed = parseFloat(clean)
    if (isNaN(parsed)) return null
    return parsed
  }

  const rawText = ref(formatToDisplay(props.modelValue))

  watch(
    () => props.modelValue,
    (newVal) => {
      // Only sync if the numerical value changed to avoid interrupting user typing
      const currentParsed = parseToNumber(rawText.value)
      const newNum = newVal === null || newVal === undefined || newVal === '' ? null : Number(newVal)
      if (currentParsed !== newNum) {
        rawText.value = formatToDisplay(newVal)
      }
    },
  )

  const onInput = (event: Event) => {
    const input = event.target as HTMLInputElement
    let value = input.value

    // Allow digits, one comma or dot
    // If dot typed, convert to comma for consistent Indonesian UX
    value = value.replace(/\./g, ',')

    // Remove any character that is not a digit or comma
    value = value.replace(/[^0-9,]/g, '')

    // Allow at most one comma
    const parts = value.split(',')
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('')
    }

    rawText.value = value
    input.value = value

    const num = parseToNumber(value)
    if (num === null) {
      emit('update:modelValue', null)
    } else {
      let finalNum = num
      if (props.max !== undefined && finalNum > props.max) {
        finalNum = props.max
      }
      if (props.min !== undefined && finalNum < props.min) {
        finalNum = props.min
      }
      emit('update:modelValue', finalNum)
    }
  }

  const onBlur = () => {
    // On blur, if there's a trailing comma, remove it
    if (rawText.value.endsWith(',')) {
      rawText.value = rawText.value.slice(0, -1)
    }
    const num = parseToNumber(rawText.value)
    if (num !== null) {
      if (props.max !== undefined && num > props.max) {
        rawText.value = formatToDisplay(props.max)
        emit('update:modelValue', props.max)
      } else if (props.min !== undefined && num < props.min) {
        rawText.value = formatToDisplay(props.min)
        emit('update:modelValue', props.min)
      }
    }
  }
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-red-600">
        *
      </span>
    </label>

    <div class="relative flex items-center">
      <input
        :id="inputId"
        type="text"
        inputmode="decimal"
        class="form-input font-mono"
        :class="{ '!pr-10': suffix }"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="rawText"
        autocomplete="off"
        @input="onInput"
        @blur="onBlur">

      <span
        v-if="suffix"
        class="suffix">
        {{ suffix }}
      </span>
    </div>

    <p
      v-if="note"
      class="text-[11px] text-gray-400 mt-1">
      * {{ note }}
    </p>

    <app-error-msg :message="error" />
  </div>
</template>

<style lang="postcss" scoped>
  .suffix {
    @apply absolute right-4 text-gray-400 pointer-events-none text-sm z-10;
  }
</style>
